import { ArrowDown, Shield, Award, Clock, Users, Star, CheckCircle, Phone, Home, Building2, TreePine } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HeroSection() {
  const trustBadges = [
    { icon: Award, label: "ISA Certified", sublabel: "Arborists" },
    { icon: Shield, label: "Fully Licensed", sublabel: "& Insured" },
    { icon: Users, label: "Expert Team", sublabel: "Since 2015" },
    { icon: Clock, label: "24/7", sublabel: "Emergency" },
  ];

  return (
    <section
      className="hero-section relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Full-Width Hero Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/3011574/pexels-photo-3011574.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-[#0A1628]/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="max-w-2xl">
            {/* Trust Indicator */}
            <div className="inline-flex items-center gap-3 bg-[#10B981]/20 border border-[#10B981]/50 px-4 py-2 mb-8 animate-fade-in-up">
              <Star className="w-4 h-4 text-[#D4A853]" fill="#D4A853" />
              <span className="font-['Cinzel'] text-[#D4A853] text-xs tracking-[0.2em] uppercase">
                Paradise Valley & Scottsdale's Most Trusted
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8 animate-fade-in-up animation-delay-100">
              The Gold Standard
              <br />
              <span className="gradient-text">in Tree Care</span>
            </h1>

            {/* Subheading */}
            <p className="text-white/80 text-lg sm:text-xl max-w-xl mb-8 leading-relaxed animate-fade-in-up animation-delay-200">
              ISA Certified Arborists protecting Arizona's most prestigious estates. 
              Expert tree care, wildfire prevention, and 24/7 emergency response.
            </p>

            {/* Who We Serve */}
            <div className="flex flex-wrap gap-4 mb-10 animate-fade-in-up animation-delay-250">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <Home className="w-4 h-4 text-[#10B981]" />
                <span className="text-white text-sm">Homeowners</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <Building2 className="w-4 h-4 text-[#D4A853]" />
                <span className="text-white text-sm">Hotels & Resorts</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2">
                <TreePine className="w-4 h-4 text-[#10B981]" />
                <span className="text-white text-sm">Golf Courses</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in-up animation-delay-300">
              <Button
                asChild
                className="bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white rounded-none px-10 py-7 uppercase tracking-[0.2em] text-sm font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all duration-300 hover:-translate-y-1"
                data-testid="hero-cta-primary"
              >
                <a href="tel:4803517775" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Call (480) 351-7775
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A1628] rounded-none px-10 py-7 uppercase tracking-[0.2em] text-sm font-bold transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
              <a href="#contact">Free Assessment</a>
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up animation-delay-400">
            {trustBadges.map((badge, index) => (
              <div
                key={index}
                className="trust-badge flex items-center gap-4 px-5 py-4"
              >
                <div className="w-12 h-12 bg-[#10B981]/20 flex items-center justify-center">
                  <badge.icon className="w-6 h-6 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">{badge.label}</p>
                  <p className="text-white/60 text-xs">{badge.sublabel}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0A1628]/90 backdrop-blur-sm border-t border-[#10B981]/20 py-6 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: "1,500+", label: "Properties Protected" },
              { number: "10+", label: "Years Excellence" },
              { number: "100%", label: "Satisfaction Rate" },
              { number: "24/7", label: "Emergency Ready" },
            ].map((stat, index) => (
              <div key={index}>
                <p className="stat-number">{stat.number}</p>
                <p className="stat-label">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <a
        href="#services"
        className="absolute bottom-28 left-1/2 -translate-x-1/2 flex flex-col items-center text-white/50 hover:text-[#10B981] transition-colors z-20"
        data-testid="scroll-indicator"
      >
        <span className="text-xs uppercase tracking-[0.2em] mb-2">Explore</span>
        <ArrowDown className="w-5 h-5 animate-bounce" />
      </a>
    </section>
  );
}
